import * as React from 'react';
import { useStyletron } from '../../Themes';
import {
	applicationPhaseStyle,
	applicationLanguageStyle,
	applicationProcedureStyle,
	applicationNumberDossierLines,
	applicationNumberDossierNumber,
	getPhaseDetails,
	cssApplicationNumberContainer,
	applicationNumberPublicationNumber,
} from './styles';
import { IDummyProps, TCopiedValueLocation } from './types';
import { StatefulTooltip as Tooltip } from '../Tooltip';
import { validPhases, isPhaseValid, phaseChar, procedureTypes, validProcedureTypes } from './utils';
import * as meta from '../../meta-data.json';

function ViewLocal(props: IDummyProps) {
	const {
		phase = '',
		language = 'EN',
		contextMenu,
		number,
		dossierNumber,
		publicationNumber,
		procedureType = 'ns_default',
		applicationNumberCopyCallback,
		dossierNumberCopyCallback,
		publicationNumberCopyCallback,
		formatApplicationNumberBeforeCopyCallback,
		formatDossierNumberBeforeCopyCallback,
		formatPublicationNumberBeforeCopyCallback,
		phaseProps = {},
		dossierNumberProps = {},
		publicationNumberProps = {},
		languageProps = {},
	} = props;
	const {
		className: appNumberClassName,
		onClick: onClickAppNumber,
		onKeyDown: onKeyDownAppNumber,
		...restPhaseProps
	} = phaseProps;
	const {
		className: dossierClassNames,
		onClick: onClickDossier,
		onKeyDown: onKeyDownDossier,
		...restDossierNumberProps
	} = dossierNumberProps;
	const {
		className: publicationClassNames,
		onClick: onClickPublication,
		onKeyDown: onKeyDownPublication,
		...restPublicationNumberProps
	} = publicationNumberProps;
	const {
		className: languageClassName,
		onClick: onClickLanguage,
		onKeyDown: onKeyDownLanguage,
		...restLanguageProps
	} = languageProps;

	const ref = React.useRef<HTMLDivElement | null>(null);
	const [css, theme]: any = useStyletron();

	const APPLICATION_NUMBER_TAG = 'applicationNumber';
	const DOSSIER_NUMBER_TAG = 'dossierNumber';
	const PUBLICATION_NUMBER_TAG = 'publicationNumber';

	const callCallbacksProp = (value: string | unknown, copiedValueLocation: TCopiedValueLocation) => {
		if (applicationNumberCopyCallback && copiedValueLocation === APPLICATION_NUMBER_TAG) {
			applicationNumberCopyCallback(value);
		}

		if (dossierNumberCopyCallback && copiedValueLocation === DOSSIER_NUMBER_TAG) {
			dossierNumberCopyCallback(value);
		}

		if (publicationNumberCopyCallback && copiedValueLocation === PUBLICATION_NUMBER_TAG) {
			publicationNumberCopyCallback(value);
		}
	};

	const writeToClipboard = async (value: string, copiedValueLocation: TCopiedValueLocation) => {
		if (value) {
			try {
				let appNumberBeforeCoping = value;
				if (formatApplicationNumberBeforeCopyCallback && copiedValueLocation === APPLICATION_NUMBER_TAG) {
					appNumberBeforeCoping = formatApplicationNumberBeforeCopyCallback(value);
				}

				if (formatDossierNumberBeforeCopyCallback && copiedValueLocation === DOSSIER_NUMBER_TAG) {
					appNumberBeforeCoping = formatDossierNumberBeforeCopyCallback(value);
				}

				if (formatPublicationNumberBeforeCopyCallback && copiedValueLocation === PUBLICATION_NUMBER_TAG) {
					appNumberBeforeCoping = formatPublicationNumberBeforeCopyCallback(value);
				}

				await navigator.clipboard.writeText(appNumberBeforeCoping);
				callCallbacksProp(value, copiedValueLocation);
			} catch (error) {
				callCallbacksProp(error, copiedValueLocation);
			}
		}
	};

	const handleOnKeyDown = (
		event: React.KeyboardEvent<HTMLButtonElement>,
		value: string,
		copiedValueLocation: TCopiedValueLocation,
	) => {
		if ([' ', 'Enter'].includes(event.key)) {
			writeToClipboard(value, copiedValueLocation);
		}
	};

	/**
	 * - we don't display the phase when it is not defined (e.g. phase is empty)
	 * - phase !== 'undefined' is for batavia documentation
	 */
	const isPhaseDisplayed = phase.length === 0 ? false : phase.length > 0 && phase !== 'undefined';
	let phaseLabel = '';
	let phaseCssRules;
	const codePhase = phase && phase.length > 0 ? phaseChar(phase) : '';

	// Phase might be defined with an unexpected phase
	if (phase.length > 0 && phase !== 'undefined' && !isPhaseValid) {
		console.warn(
			"The phase value has an unexpected value. It accepts any value of 'search', 'examination', 'opposition', 'appeal', 'post grand'",
		);
	}

	if (isPhaseDisplayed) {
		phaseLabel = validPhases.includes(phase) ? getPhaseDetails(phase.toLowerCase(), theme).label : '';
		const validPhase = validPhases.includes(phase) ? phase : 'default';
		phaseCssRules = css(applicationPhaseStyle(validPhase, theme));
	}

	const validatedProcedure = validProcedureTypes.includes(procedureType.toUpperCase()) ? procedureType : 'ns_default';
	const phaseTooltip = `${phaseLabel} phase`;
	const procedureCode = procedureTypes[validatedProcedure.toUpperCase()].code || '';
	const limitedAppNumber = typeof number === 'string' ? number.substring(0, 24) : '';
	const labelApplicationNumber = `${limitedAppNumber}`;

	return (
		<div data-epo={`epo-application-number-${meta.version}`} className={css({ display: 'inline-flex', flexDirection: 'column' })}>
			<div
				className={css({ display: 'flex' })}
			>
				<div className={css(cssApplicationNumberContainer(theme))}
					onClick={() => writeToClipboard(labelApplicationNumber, APPLICATION_NUMBER_TAG)}>
					{isPhaseDisplayed && (
						<Tooltip content={phaseTooltip} placement="top">
							<button
								className={phaseCssRules}
								data-epo={`epo-phase-application-number-${meta.version}`}
								{...restPhaseProps}
							>
								{codePhase}
							</button>
						</Tooltip>
					)}
					<Tooltip content="Click to copy the number" placement="top">
						<button
							className={css(applicationProcedureStyle(procedureCode, theme, phase))}
							onKeyDown={event => handleOnKeyDown(event, labelApplicationNumber, APPLICATION_NUMBER_TAG)}
							data-epo={`"epo-code-application-number-${meta.version}`}
						>
							{labelApplicationNumber}
						</button>
					</Tooltip>
					{language && (
						<Tooltip content="Procedural language" placement="top">
							<button
								className={css(applicationLanguageStyle(procedureCode, theme))}
								data-epo={`epo-language-application-number-${meta.version}`}
								{...restLanguageProps}
							>
								{language}
							</button>
						</Tooltip>
					)}
				</div>
				<span ref={ref}></span>
				{contextMenu}
			</div>
			{
				dossierNumber && (
					<Tooltip content="Click to copy the dossier number" placement="bottom">
						<div className={css({ display: 'flex', position: 'relative' })}>
							<div className={css(applicationNumberDossierLines(procedureCode, theme))}></div>
							<button
								className={css(applicationNumberDossierNumber(theme))}
								onKeyDown={event => handleOnKeyDown(event, dossierNumber, DOSSIER_NUMBER_TAG)}
								data-epo={`epo-dossier-number-${meta.version}`}
								onClick={() => writeToClipboard(dossierNumber, DOSSIER_NUMBER_TAG)}
								{...restDossierNumberProps}
							>
								{dossierNumber}
							</button>
						</div>
					</Tooltip>
				)
			}
			{
				publicationNumber && (
					<Tooltip content="Click to copy the publication number" placement="bottom">
						<div className={css({ display: 'flex', position: 'relative' })}>
							<div className={css(applicationNumberDossierLines(procedureCode, theme))}></div>
							<button
								className={css(applicationNumberPublicationNumber(theme))}
								onKeyDown={event => handleOnKeyDown(event, publicationNumber, PUBLICATION_NUMBER_TAG)}
								data-epo={`epo-publication-number-${meta.version}`}
								onClick={() => writeToClipboard(publicationNumber, PUBLICATION_NUMBER_TAG)}
								{...restPublicationNumberProps}
							>
								{publicationNumber}
							</button>
						</div>
					</Tooltip>
				)
			}
		</div >
	);
}

export const View = React.memo(ViewLocal);
